import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { isUndefined } from 'lodash';
import { useParams } from 'react-router';

import { DEFAULT_RETRY_DELAY, DEFAULT_RETRY_NUMBERS } from '../../../../config/query-client';
import useCurrentClinic from '../../../../hooks/useCurrentClinic';
import { getDate } from '../../../timeService';
import { IAdministrationInfo, IEpisodeOfCare } from '../types';
import useBillingPeriodService from './billingPeriod';
import useCareLocationService from './careLocation';
import useDischargeService from './discharge';

export default function useAdministrationService(patientUuid1?:string) {
  const { patientUuid = patientUuid1 } = useParams<{ patientUuid: string }>();//TODO: change to arg
  const discharge = useDischargeService();
  const billingPeriod = useBillingPeriodService();
  const careLocation = useCareLocationService();
  const { clinic } = useCurrentClinic();
  const result = useQuery<IAdministrationInfo>({
    queryKey: [
      'camel',
      'patient_profile',
      'administration',
      patientUuid,
      { clinic_id: clinic?.uuid },
    ],
  });
  const currentEpisodeOfCare = (result?.data?.episodes ?? []).find(
    (episode) => episode.isCurrent
  ) as IEpisodeOfCare | undefined;

  const updateAction = useMutation({
    mutationFn: async ({
      data,
      reValidateQuery,
    }: {
      data: Partial<IEpisodeOfCare>;
      reValidateQuery?: boolean;
    }) => {
      await axios.put(
        `/api/camel/patient_profile/administration/episode_of_care/${data.uuid}`,
        data
      );
      if (reValidateQuery) {
        result.refetch();
      }
    },
  });

  return {
    refetch: () => result.refetch(),
    data: {
      ...(result?.data ?? ({} as IAdministrationInfo)),
      currentEpisodeOfCare,
      currentPayPeriod: (result?.data?.billingPeriods ?? []).find(
        (period) =>
          period.isCurrent &&
          !isUndefined(period.end) &&
          getDate(period.end).isAfter(getDate(), 'day')
      ),
    },
    currentEpisodeOfCare,
    episodes: result?.data?.episodes,
    isLoading: result?.isLoading ?? false,
    isRefetching: result?.isRefetching ?? false,
    isError: result?.isError ?? false,
    editEpisodeOfCare: updateAction.mutateAsync,
    ...discharge,
    ...billingPeriod,
    ...careLocation,
  };
}
